import { put, call, takeLatest } from 'redux-saga/effects';
import {
  CASE_ASK_AI_SSE_RESET,
  CASE_FETCH_ASK_AI_SSE_CHAT_MESSAGES_BEGIN,
  CASE_FETCH_ASK_AI_SSE_CHAT_MESSAGES_SUCCESS,
  CASE_FETCH_ASK_AI_SSE_CHAT_MESSAGES_FAILURE,
  CASE_FETCH_ASK_AI_SSE_CHAT_MESSAGES_DISMISS_FEEDBACK,
} from './constants';
import api from 'common/api';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  createPromiseAction,
  resolvePromiseAction,
  rejectPromiseAction,
} from '@adobe/redux-saga-promise';

export const fetchAskAiSseChatMessages = createPromiseAction(
  CASE_FETCH_ASK_AI_SSE_CHAT_MESSAGES_BEGIN,
);

export function dismissFetchAskAiSseChatMessagesFeedback() {
  return {
    type: CASE_FETCH_ASK_AI_SSE_CHAT_MESSAGES_DISMISS_FEEDBACK,
  };
}

const safeParseDate = (value) => {
  const t = Date.parse(String(value || ''));
  return Number.isFinite(t) ? t : Date.now();
};

const normalizeStringArray = (value) => {
  if (Array.isArray(value)) {
    return value.map((v) => String(v ?? '').trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return [];
};

const buildCompareFromPivotAndSet = (pivot, comparisonSet) => {
  const pivotValue = normalizeStringArray(pivot)[0] || '';
  const compareValues = normalizeStringArray(comparisonSet);
  if (!pivotValue && compareValues.length === 0) return [];
  const combined = pivotValue ? [pivotValue, ...compareValues] : compareValues;
  const seen = new Set();
  return combined.filter((value) => {
    if (!value || seen.has(value)) return false;
    seen.add(value);
    return true;
  });
};

const extractCompareFiles = (value) => {
  if (!value) return [];
  if (typeof value === 'object') {
    const pivotValue =
      value?.pivot ??
      value?.pivotFile ??
      value?.primaryFile ??
      value?.metadata?.pivot ??
      value?.metadata?.pivotFile;
    const comparisonSetValue =
      value?.comparisonSet ??
      value?.comparison_set ??
      value?.comparisonFiles ??
      value?.metadata?.comparisonSet ??
      value?.metadata?.comparison_set;
    const fromPivotAndSet = buildCompareFromPivotAndSet(pivotValue, comparisonSetValue);
    if (fromPivotAndSet.length > 0) return fromPivotAndSet;
    if (Array.isArray(value?.files) || typeof value?.files === 'string') {
      return normalizeStringArray(value.files);
    }
  }
  if (Array.isArray(value?.files) || typeof value?.files === 'string') {
    return normalizeStringArray(value.files);
  }
  return normalizeStringArray(value);
};

// Normalize citations from API response (CitationX keys) to numeric keys.
// Returns an object with numeric string keys like { "1": {...}, "2": {...} }.
export const normalizeCitations = (citations) => {
  if (!citations || typeof citations !== 'object') return {};
  const normalized = {};
  for (const key of Object.keys(citations)) {
    // Extract numeric portion from CitationX format
    const match = key.match(/Citation(?:Match)?(\d+)/);
    if (match) {
      const numericKey = match[1];
      normalized[numericKey] = citations[key];
    } else {
      // If key is already numeric or other format, keep as-is
      normalized[key] = citations[key];
    }
  }
  return normalized;
};

// Convert REST chat/messages payload into the structure AskAISSE already renders.
export const mapChatMessagesToAskAiSseHistory = (messages, citations = {}, compareFiles = []) => {
  const list = Array.isArray(messages) ? messages : [];
  const normalizedCitations = normalizeCitations(citations);
  const history = [];
  const fallbackCompare = Array.isArray(compareFiles) ? compareFiles : [];

  let i = 0;
  while (i < list.length) {
    const m = list[i];
    if (!m || m.role !== 'user') {
      i += 1;
      continue;
    }

    const userMsg = m;
    let assistantMsg = null;
    let j = i + 1;
    while (j < list.length) {
      const next = list[j];
      if (next?.role === 'assistant') {
        assistantMsg = next;
        break;
      }
      if (next?.role === 'user') break; // unexpected ordering; treat as next prompt
      j += 1;
    }

    const compareFromMessage = extractCompareFiles(
      userMsg?.parameters ||
        userMsg?.metadata?.parameters ||
        userMsg?.comparison_set ||
        userMsg?.comparisonSet ||
        userMsg?.compare ||
        userMsg?.metadata?.comparison_set ||
        userMsg?.metadata?.comparisonSet ||
        userMsg?.metadata?.compare ||
        assistantMsg?.comparison_set ||
        assistantMsg?.comparisonSet ||
        assistantMsg?.compare ||
        assistantMsg?.metadata?.comparison_set ||
        assistantMsg?.metadata?.comparisonSet ||
        assistantMsg?.metadata?.compare,
    );
    const effectiveCompare =
      compareFromMessage.length > 0 ? compareFromMessage : fallbackCompare;

    history.push({
      id: userMsg.id ?? safeParseDate(userMsg.timestamp),
      prompt: userMsg.content ?? '',
      assistant: assistantMsg?.content ?? '',
      citationsBySource: normalizedCitations,
      status: assistantMsg ? 'complete' : 'loading',
      error: null,
      createdAt: safeParseDate(userMsg.timestamp),
      compare: effectiveCompare.length > 0 ? { files: effectiveCompare } : null,
    });

    i = assistantMsg ? j + 1 : i + 1;
  }

  return history;
};

export function* doFetchAskAiSseChatMessages(action) {
  const {
    payload: { userId, caseId, chatId },
  } = action;

  if (!userId || !chatId) {
    const message = 'Missing userId or chatId for chat/messages fetch';
    yield put({ type: CASE_FETCH_ASK_AI_SSE_CHAT_MESSAGES_FAILURE, error: message });
    yield call(rejectPromiseAction, action, message);
    return;
  }

  const url = `/cases/${encodeURIComponent(String(caseId))}/chats/messages?chat_id=${encodeURIComponent(String(chatId))}`;
  const res = yield call(api.get, url);

  if (res && res.error) {
    yield put({ type: CASE_FETCH_ASK_AI_SSE_CHAT_MESSAGES_FAILURE, error: res.error });
    return yield call(rejectPromiseAction, action, res.error);
  }

  yield put({
    type: CASE_FETCH_ASK_AI_SSE_CHAT_MESSAGES_SUCCESS,
    data: res,
  });
  yield call(resolvePromiseAction, action, res);
}

export function* watchFetchAskAiSseChatMessages() {
  yield takeLatest(fetchAskAiSseChatMessages, doFetchAskAiSseChatMessages);
}

export function useFetchAskAiSseChatMessages() {
  const dispatch = useDispatch();
  const {
    askAiSseChatMessagesResponse,
    fetchAskAiSseChatMessagesPending,
    fetchAskAiSseChatMessagesError,
  } = useSelector(
    (state) => ({
      askAiSseChatMessagesResponse: state.case.askAiSseChatMessagesResponse,
      fetchAskAiSseChatMessagesPending: state.case.fetchAskAiSseChatMessagesPending,
      fetchAskAiSseChatMessagesError: state.case.fetchAskAiSseChatMessagesError,
    }),
    shallowEqual,
  );

  const boundFetch = useCallback(
    ({ userId, chatId }) => dispatch(fetchAskAiSseChatMessages({ userId, chatId })),
    [dispatch],
  );

  const boundDismiss = useCallback(
    () => dispatch(dismissFetchAskAiSseChatMessagesFeedback()),
    [dispatch],
  );

  return {
    askAiSseChatMessagesResponse,
    fetchAskAiSseChatMessages: boundFetch,
    fetchAskAiSseChatMessagesPending,
    fetchAskAiSseChatMessagesError,
    dismissFetchAskAiSseChatMessagesFeedback: boundDismiss,
  };
}

export function reducer(state, action) {
  switch (action.type) {
    case CASE_ASK_AI_SSE_RESET:
      return {
        ...state,
        fetchAskAiSseChatMessagesPending: false,
        fetchAskAiSseChatMessagesError: null,
        askAiSseChatMessagesResponse: null,
      };

    case CASE_FETCH_ASK_AI_SSE_CHAT_MESSAGES_BEGIN + '.TRIGGER':
      return {
        ...state,
        fetchAskAiSseChatMessagesPending: true,
        fetchAskAiSseChatMessagesError: null,
      };

    case CASE_FETCH_ASK_AI_SSE_CHAT_MESSAGES_SUCCESS: {
      const data = action.data || null;
      const nextChatId = data?.chatId ?? data?.chat_id ?? null;
      const normalizedCitations = normalizeCitations(data?.citations);
      const responseCompareFiles = (() => {
        if (Array.isArray(data?.messages)) {
          const firstUserMessage = data.messages.find((msg) => msg?.role === 'user');
          const fromUserParams = extractCompareFiles(firstUserMessage?.parameters);
          if (fromUserParams.length > 0) return fromUserParams;
        }
        const directCompare = extractCompareFiles(
          data?.comparison_set ||
            data?.comparisonSet ||
            data?.compare?.files ||
            data?.compare ||
            data?.metadata?.comparison_set ||
            data?.metadata?.comparisonSet ||
            data?.metadata?.compare,
        );
        if (directCompare.length > 0) return directCompare;
        const pivot = data?.pivot ?? data?.compare?.pivot ?? data?.metadata?.pivot;
        return pivot ? [String(pivot)] : [];
      })();
      const mappedHistory = mapChatMessagesToAskAiSseHistory(
        data?.messages,
        data?.citations,
        responseCompareFiles,
      );
      return {
        ...state,
        fetchAskAiSseChatMessagesPending: false,
        fetchAskAiSseChatMessagesError: null,
        askAiSseChatMessagesResponse: data,
        askAiSseChatId: nextChatId || state.askAiSseChatId,
        // Replace chat history for hydration
        askAiSseHistory: mappedHistory,
        // Ensure we aren't showing stale streaming state after hydration
        askAiSseStatus: 'idle',
        askAiSsePrompt: '',
        askAiSseAssistantText: '',
        askAiSseProgress: [],
        askAiSseProgressMessage: '',
        askAiSseError: null,
        askAiSseHasFirstChunk: false,
        askAiSseCurrentId: null,
        // Store normalized citations for the hydrated chat
        askAiSseCitationsBySource: normalizedCitations,
      };
    }

    case CASE_FETCH_ASK_AI_SSE_CHAT_MESSAGES_FAILURE:
      return {
        ...state,
        fetchAskAiSseChatMessagesPending: false,
        fetchAskAiSseChatMessagesError: action.error || 'Failed to fetch chat messages',
      };

    case CASE_FETCH_ASK_AI_SSE_CHAT_MESSAGES_DISMISS_FEEDBACK:
      return {
        ...state,
        fetchAskAiSseChatMessagesError: null,
      };

    default:
      return state;
  }
}
